import React, { memo, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  ClickAwayListener,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import { CheckIcon, PlusIcon } from "src/Icons";
import { EmployeeArrowRight } from "src/Icons/EmployeeArrowRight";
import { FolderArrowIcon } from "src/Icons/FolderArrowIcon";
import { FolderCheckIcon } from "src/Icons/FolderCheckIcon";
import { ExpandButton } from "src/styledComponents/ExpandButton";
import { getAvailableStudioTemplates } from "src/utils/helpers";

interface NestedTreeForSelectorProps{
  preOpenedItems: {cat: number | null, subcat: number | null, service: number | null,};
  onServiceSelect: (id: string) => void;
  categoryTemplates:Array<any>;
}

const NestedTreeForSelector = ({
  preOpenedItems,
  onServiceSelect,
  categoryTemplates,
} : NestedTreeForSelectorProps) => {

  const [selectedCat, setSelectedCat] = useState<number | null>(preOpenedItems.cat);
  const [selectedSubcat, setSelectedSubcat] = useState<number | null>(preOpenedItems.subcat);
  const [selectedService, setSelectedService] = useState<number | null>(preOpenedItems.service);

  const handleCategoryClick = (id: any) => {
    setSelectedCat(selectedCat === id ? null : id);
    setSelectedSubcat(null);
    setSelectedService(null);
  };

  const handleSubcatClick = (id: any) => {
    setSelectedSubcat(selectedSubcat === id ? null : id);
    setSelectedService(null);
  };

  const handleServiceClick = (id: any) => {
    setSelectedService(selectedService === id ? null : id);
    onServiceSelect(selectedService === id ? '' : `${id}`)
  };

  return (
    <List sx={{padding: 0}}>
      {categoryTemplates.map((category, index) => (
        <div key={index}>
          <ListItem
            onClick={() => handleCategoryClick(category.id)}
            alignItems="center"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: "#FFF8FA",
              },
            }}
          >
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              sx ={{
                marginBottom: '8px',
                marginTop: '8px'
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  width: "75%",
                  color: selectedCat === category.id ? "#BF295C" : "black",
                }}
              >
                {selectedCat === category.id ? (
                  <FolderCheckIcon
                    sx={{
                      width: "18px",
                      height: "18px",
                      marginRight: "5px",
                    }}
                  />
                ) : (
                  <FolderArrowIcon
                    sx={{
                      width: "18px",
                      height: "18px",
                      marginRight: "5px",
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "400",
                    whiteSpace: "nowrap",
                    paddingLeft: "5px",
                  }}
                >
                  {category.title}
                </Typography>
              </Box>
            </Box>
          </ListItem>

          {selectedCat === category.id && (
            <>
              {category.children.map((child: any) => (
                <div key={child.id}>
                  <ListItem
                    onClick={() => handleSubcatClick(child.id)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: "transparent",
                      display: 'flex',
                      justifyContent: 'space-between',
                      '&:hover': {
                        backgroundColor: "#FFF8FA",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        flexDirection: "row",
                        color: selectedSubcat === child.id ? '#BF295C' : 'black',
                        paddingLeft: "25px",
                      }}
                    >
                      <EmployeeArrowRight
                        sx={{
                          width: "18px",
                          height: "18px",
                          marginRight: "10px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {child.title.toUpperCase()}
                      </Typography>
                    </Box>
                  </ListItem>

                  {(
                    selectedSubcat === child.id &&
                  child.services?.length > 0) &&
                (
                  <>
                    {child.services.map((item: any) => (
                      <div key={item.id}>
                        <ListItem
                          onClick={() => handleServiceClick(item.id)}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            backgroundColor: "transparent",
                            paddingLeft: '66px',
                            '&:hover': {
                              backgroundColor: "#FFF8FA",
                            },
                            color: selectedService === item.id ? '#BF295C' : 'black',
                          }}
                        >
                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            {selectedService === item.id ?
                              <CheckIcon
                                sx={{
                                  width: "15px",
                                  height: "15px",
                                  marginRight: "10px",
                                }}
                              />
                              :
                              <PlusIcon
                                sx={{
                                  width: "15px",
                                  height: "15px",
                                  marginRight: "10px",
                                }}
                              />
                            }
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "400",
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: "400",
                                color: "#828282",
                                marginLeft: "5px",
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{Math.floor(item.duration / 60)}</span>h&nbsp;
                              <span style={{ fontWeight: 600 }}>{item.duration % 60}</span>min
                            </Typography>
                            <InfoIcon
                              sx={{
                                width: "18px",
                                height: "18px",
                                color: "#B9B9B9",
                                marginLeft: "8px",
                              }}
                            />
                          </Box>
                        </ListItem>
                      </div>
                    ))}
                  </>
                )}
                </div>
              ))}
              {category.services?.length > 0 && (
                <>
                  {category.services?.map((item: any) => (
                    <div key={item.id}>
                      <ListItem
                        onClick={() => {
                          handleServiceClick(item.id)
                          setSelectedSubcat(null)
                        }
                        }
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          backgroundColor: "transparent",
                          paddingLeft: '42px',
                          '&:hover': {
                            backgroundColor: "#FFF8FA",
                          },
                          color: selectedService === item.id ? '#BF295C' : 'black',
                        }}
                      >
                        <Box
                          sx={{
                            alignItems: "center",
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          {selectedService === item.id ?
                            <CheckIcon
                              sx={{
                                width: "15px",
                                height: "15px",
                                marginRight: "10px",
                              }}
                            />
                            :
                            <PlusIcon
                              sx={{
                                width: "15px",
                                height: "15px",
                                marginRight: "10px",
                              }}
                            />
                          }
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: "400",
                            }}
                          >
                            {item.title}
                          </Typography>

                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: "400",
                              color: "#828282",
                              marginLeft: "5px",
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>{Math.floor(item.duration / 60)}</span>h&nbsp;
                            <span style={{ fontWeight: 600 }}>{item.duration % 60}</span>min
                          </Typography>
                          <InfoIcon
                            sx={{
                              width: "18px",
                              height: "18px",
                              color: "#B9B9B9",
                              marginLeft: "8px",
                            }}
                          />
                        </Box>
                      </ListItem>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      ))}
    </List>
  );
}


interface NewDefaultNestedSelectorProps {
  selectedValue: any;
  valuesToSelectFrom: Array<any>;
  onChanged: (value: string) => void;
  title: string;
  filterArray?: string[];
  containerStyle?: any;
  leftContainerStyle?: any;
  rightContainerStyle?: any;
}

const NewDefaultNestedSelector = memo(({
  selectedValue,
  title,
  valuesToSelectFrom,
  onChanged,
  containerStyle,
  leftContainerStyle,
  rightContainerStyle,
}: NewDefaultNestedSelectorProps) => {

  const [isOpen, setIsOpen] = useState(false);

  const pathObject = getAvailableStudioTemplates(valuesToSelectFrom, [+selectedValue])[0] || {};
  // in our pathObject we always have 1 service(selectedValue), which is either in category or subcategory
  const currentValueObject = pathObject.services?.length > 0 ? pathObject.services[0] :
    pathObject.children?.length > 0 ? pathObject.children[0].services[0] : {};

  const getCurrentPath = (endpointId: number | null, toCurrentValuePath: any) => {
    const result = { cat: null, subcat: null, service: null };

    if (toCurrentValuePath && toCurrentValuePath.services) {
      const service = toCurrentValuePath.services[0]
      if (service) {
        result.cat = toCurrentValuePath.id;
        result.service = service.id;
        return result;
      }
    }

    if (toCurrentValuePath && toCurrentValuePath.children && toCurrentValuePath.children.length > 0) {
      for (const child of toCurrentValuePath.children) {
        const temp: any = getCurrentPath(endpointId, child);
        if (temp) {
          result.cat = toCurrentValuePath.id;
          result.subcat = temp.cat;
          result.service = temp.service;
          return result;
        }
      }
    }

    return result;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        ...containerStyle,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "46px",
          minWidth: 170,
          paddingLeft: "16px",
          backgroundColor: "#F3F3F3",
          color: "#1F1F1F",
          border: "1px solid #D6D6DB",
          borderRight: "none",
          borderTopLeftRadius: "6px",
          borderBottomLeftRadius: "6px",
          ...leftContainerStyle,
        }}
      >
        <Typography
          sx={{
            fontWeight: "500",
            fontSize: "16px",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          border: "1px solid #D6D6DB",
          borderLeft: "none",
          borderTopRightRadius: "6px",
          borderBottomRightRadius: "6px",
          ...(isOpen && {
            borderBottomRightRadius: "none",
          }),
          ...rightContainerStyle,
        }}
      >
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <Paper
            sx={{
              position: 'absolute',
              width: "100%",
              maxWidth: "535px",
              boxShadow: isOpen ? "0px 4px 16px 0px rgba(41, 66, 90, 0.12)" : 0,
              zIndex: isOpen ? 2 : 0,
            }}
          >
            <ExpandButton
              onClick={() => setIsOpen(!isOpen)}
              value={currentValueObject}
              isOpen={isOpen}
            />
            {isOpen &&
            <NestedTreeForSelector
              preOpenedItems={getCurrentPath(
                +selectedValue || null,
                pathObject
              )}
              onServiceSelect={onChanged}
              categoryTemplates={valuesToSelectFrom}
            />
            }
          </Paper>
        </ClickAwayListener>
      </Box>
    </Box>
  );
});

export {NewDefaultNestedSelector}